import React, { useState, useCallback, useEffect } from 'react';
import type { ScriptFormData } from './types';
import { VideoStyle, CtaOption, VideoType } from './types';
import { Header } from './components/Header';
import { ScriptForm } from './components/ScriptForm';
import { ScriptDisplay } from './components/ScriptDisplay';
import { AuthModal } from './components/AuthModal';
import { ApiService, type SavedTemplate, type UsageInfo } from './services/apiService';
import { auth, supabase, isDemoMode } from './lib/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';

// Simplified user type for UI state
interface AppUser {
  id: string;
  email: string;
  isLoggedIn: boolean;
}

const App: React.FC = () => {
  // Auth state (using Supabase)
  const [user, setUser] = useState<AppUser>({ id: '', email: '', isLoggedIn: false });
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Script Generator State
  const [formData, setFormData] = useState<ScriptFormData>({
    videoType: VideoType.Vehicle,
    make: '',
    model: '',
    year: '',
    keyFeatures: '',
    targetAudience: '',
    videoStyle: VideoStyle.Informative,
    cta: CtaOption.Comment,
  });
  const [generatedScript, setGeneratedScript] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Usage tracking
  const [usage, setUsage] = useState<UsageInfo | null>(null);

  // Library State
  const [showLibrary, setShowLibrary] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState<SavedTemplate[]>([]);
  const [isLibraryLoading, setIsLibraryLoading] = useState(false);

  // LocalStorage key for form persistence
  const FORM_STORAGE_KEY = 'scp_script_form_data';

  // Load form data from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(FORM_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormData(prev => ({ ...prev, ...parsed }));
      }
    } catch (e) {
      console.error('Failed to load saved form data:', e);
    }
  }, []);

  // Save form data to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.error('Failed to save form data:', e);
    }
  }, [formData]);

  // Auto-dismiss error after 7 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 7000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-dismiss success message after 4 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const supabaseUser = await auth.getUser();
        if (supabaseUser) {
          setUser({
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            isLoggedIn: true,
          });
        }
      } catch (err) {
        console.error('Auth init error:', err);
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          isLoggedIn: true,
        });
      } else {
        setUser({ id: '', email: '', isLoggedIn: false });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto-show login modal for unauthenticated users
  useEffect(() => {
    if (!authLoading && !user.isLoggedIn) {
      setShowAuthModal(true);
    }
  }, [authLoading, user.isLoggedIn]);

  // Fetch usage when user logs in
  useEffect(() => {
    if (user.isLoggedIn) {
      ApiService.getUsage().then(setUsage).catch(console.error);
    } else {
      setUsage(null);
    }
  }, [user.isLoggedIn]);

  const refreshLibrary = async () => {
    if (!user.isLoggedIn) return;
    setIsLibraryLoading(true);
    try {
      const data = await ApiService.getTemplates();
      setSavedTemplates(data);
    } catch (err) {
      console.error('Library sync error:', err);
    } finally {
      setIsLibraryLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      setUser({ id: '', email: '', isLoggedIn: false });
      setShowLibrary(false);
      setSavedTemplates([]);
      setUsage(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleAuthSuccess = async () => {
    // Refresh usage after login
    const usageData = await ApiService.getUsage();
    setUsage(usageData);
  };

  const handleSaveTemplate = async () => {
    if (!user.isLoggedIn || !generatedScript) return;
    setIsSaving(true);
    try {
      await ApiService.saveTemplate(formData, generatedScript);
      await refreshLibrary();
      setSuccessMessage('Template saved to your library!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  // Clear form to defaults
  const handleClearForm = () => {
    const defaultForm: ScriptFormData = {
      videoType: VideoType.Vehicle,
      make: '',
      model: '',
      year: '',
      keyFeatures: '',
      targetAudience: '',
      videoStyle: VideoStyle.Informative,
      cta: CtaOption.Comment,
    };
    setFormData(defaultForm);
    setGeneratedScript('');
    setError(null);
    localStorage.removeItem(FORM_STORAGE_KEY);
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await ApiService.deleteTemplate(id);
      setSavedTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      setError('Could not remove template.');
    }
  };

  const handleGenerateScript = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user.isLoggedIn) {
      setShowAuthModal(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    setGeneratedScript('');

    try {
      const response = await ApiService.generateScript(formData);
      setGeneratedScript(response.script);
      setUsage(response.usage);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Generation failed';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [formData, user.isLoggedIn]);

  // Show loading spinner while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0d14]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#3db8c5] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#3db8c5] font-bold uppercase text-sm">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        user={{ ...user, username: user.email }}
        onLoginClick={() => setShowAuthModal(true)}
        onLogout={handleLogout}
        onViewLibrary={() => {
          setShowLibrary(!showLibrary);
          if (!showLibrary) refreshLibrary();
        }}
        onHomeClick={() => setShowLibrary(false)}
        showLibrary={showLibrary}
        isDemoMode={isDemoMode}
      />

      <main className="flex-grow p-4 sm:p-6 lg:p-12 max-w-7xl mx-auto w-full">
        {/* Usage Banner */}
        {user.isLoggedIn && usage && (
          <div className="mb-6 p-4 bg-gradient-to-r from-[#002d5b] to-[#002d5b]/80 border border-[#3db8c5]/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-slate-300">
                Welcome, <span className="text-[#3db8c5] font-semibold">{user.email}</span>
              </span>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">
                Scripts this month: <span className="text-white font-bold">{usage.used}</span> / {usage.limit}
              </p>
              <div className="w-32 h-1.5 bg-slate-700 rounded-full mt-1 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#3db8c5] to-[#f15a24] transition-all duration-500"
                  style={{ width: `${Math.min((usage.used / usage.limit) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {showLibrary ? (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-8 border-b-2 border-[#3db8c5]/30 pb-4">
              <div className="flex items-center">
                <div className="w-3 h-8 bg-[#3db8c5] mr-3 skew-x-[-12deg]"></div>
                <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Script Vault</h2>
              </div>
              {isLibraryLoading && <span className="text-[10px] font-black text-[#3db8c5] animate-pulse">Syncing Library...</span>}
            </div>

            {savedTemplates.length === 0 ? (
              <div className="text-center py-20 bg-black/20 rounded-xl border-2 border-dashed border-[#3db8c5]/20">
                <p className="text-[#3db8c5] font-black uppercase tracking-widest opacity-50">Vault is empty</p>
                <button onClick={() => setShowLibrary(false)} className="mt-4 text-[#f15a24] text-xs font-black uppercase hover:underline">Return to Generator</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {savedTemplates.map(template => {
                  // Extract title from script (looks for **Title:** pattern)
                  const titleMatch = template.script.match(/\*\*Title:\*\*\s*(.+?)(?:\n|$)/);
                  const scriptTitle = titleMatch ? titleMatch[1].trim() : null;
                  // Clean preview: strip markdown bold markers
                  const cleanPreview = template.script.replace(/\*\*/g, '').slice(0, 200);
                  
                  return (
                    <div key={template.id} className="bg-gradient-to-br from-[#002d5b] to-[#001a3a] border border-[#3db8c5]/30 p-6 rounded-lg relative group hover:border-[#f15a24] hover:shadow-[0_0_30px_rgba(241,90,36,0.15)] transition-all duration-300">
                      {/* Decorative corner accent */}
                      <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                        <div className="absolute top-2 right-[-20px] w-16 h-4 bg-[#f15a24]/20 rotate-45"></div>
                      </div>
                      
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-[#f15a24] font-black uppercase text-sm tracking-wide">
                            {template.title || `${template.form_data.make || 'Trending'} Script`}
                          </h3>
                          {scriptTitle && (
                            <p className="text-white/80 text-xs mt-1 font-medium">"{scriptTitle}"</p>
                          )}
                          <p className="text-[10px] text-[#3db8c5]/60 font-bold uppercase mt-2">
                            {new Date(template.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="text-red-400/60 hover:text-red-400 text-[10px] font-black uppercase transition-colors opacity-0 group-hover:opacity-100"
                        >✕ Remove</button>
                      </div>
                      
                      <div className="bg-black/30 p-4 rounded-lg text-xs text-gray-400 line-clamp-2 mb-4 font-medium border-l-2 border-[#3db8c5]/50">
                        {cleanPreview}...
                      </div>
                      
                      <button
                        onClick={() => {
                          setGeneratedScript(template.script);
                          setFormData(template.form_data);
                          setShowLibrary(false);
                        }}
                        className="w-full py-3 bg-gradient-to-r from-[#3db8c5] to-[#2fa3b0] text-white text-[10px] font-black uppercase rounded-lg hover:from-[#4dc9d6] hover:to-[#3db8c5] transition-all shadow-lg"
                      >
                        ↩ Restore to Workspace
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
            <ScriptForm formData={formData} setFormData={setFormData} onSubmit={handleGenerateScript} onClear={handleClearForm} isLoading={isLoading} />
            <ScriptDisplay
              script={generatedScript}
              isLoading={isLoading}
              error={error}
              user={{ ...user, username: user.email, isLoggedIn: user.isLoggedIn }}
              onSave={handleSaveTemplate}
              isSaving={isSaving}
            />
          </div>
        )}
      </main>

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 font-bold">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />

      <footer className="text-center py-8 text-[#3db8c5]/30 text-sm font-medium uppercase tracking-widest bg-black/20 mt-auto border-t border-white/5">
        <div className="flex justify-center items-center gap-2 mb-2">
          <div className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></div>
          <span>System Online: Vercel + Supabase</span>
        </div>
        <p>Strategic Creative Intelligence Powered by Google Gemini</p>
      </footer>
    </div>
  );
};

export default App;